/**
 * Scheduled content — the state model and its pure helpers (SC-1;
 * docs/scheduled-content-contracts.md).
 *
 * `section.schedule = { showAt: <epoch ms|null>, hideAt: <epoch ms|null> }` —
 * at least one bound, `showAt < hideAt` when both, ABSENT means unscheduled.
 * Values are instants (epoch ms) resolved from the authoring browser's
 * timezone; the disclosed v1 DST edge and the additive `tz` v2 are in the
 * contracts. Worker-safe, zero imports: the exporters, the sites Worker and
 * the boundary-notify job all read schedules through THIS module.
 */

const MIN_MS = Date.UTC(2020, 0, 1);
const MAX_MS = Date.UTC(2100, 0, 1);

function bound(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const ms = Math.floor(n);
  if (ms < MIN_MS || ms > MAX_MS) return null;
  return ms;
}

/** A valid `{showAt, hideAt}` or null (= unscheduled / strip it). */
export function sanitizeSchedule(value) {
  if (!value || typeof value !== 'object') return null;
  const showAt = bound(value.showAt);
  const hideAt = bound(value.hideAt);
  if (showAt === null && hideAt === null) return null;
  if (showAt !== null && hideAt !== null && showAt >= hideAt) return null;
  return { showAt, hideAt };
}

/**
 * Normalize every section's schedule in place — invalid shapes are DELETED
 * (absence is the default, so the contentHash of a schedule-less project
 * never moves), and shared nav/footer are stripped ALWAYS (the entrance strip
 * precedent: scheduling a section that renders on every page is a different
 * feature, and v1 does not have it).
 */
export function normalizeScheduleState(state) {
  if (!state || typeof state !== 'object') return state;
  for (const key of ['navigation', 'footer']) {
    const shared = state.sharedSections?.[key];
    if (shared && 'schedule' in shared) delete shared.schedule;
  }
  for (const page of state.pages || []) {
    for (const section of page?.sections || []) {
      if (!section || !('schedule' in section)) continue;
      const clean = sanitizeSchedule(section.schedule);
      if (clean === null) delete section.schedule;
      else section.schedule = clean;
    }
  }
  return state;
}

/** True when `now` falls inside the rule's window. */
export function isInWindow(rule, now) {
  const showAt = rule?.showAt ?? null;
  const hideAt = rule?.hideAt ?? null;
  if (showAt !== null && now < showAt) return false;
  if (hideAt !== null && now >= hideAt) return false;
  return true;
}

/**
 * The sorted rule list one state produces — the SAME values the sidecar, the
 * lock's `scheduleRules` and the boundary-notify job carry (values only,
 * never an evaluation: determinism is contracts §2).
 */
export function sectionScheduleRules(state) {
  const rules = [];
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      const clean = sanitizeSchedule(section?.schedule);
      if (!clean) continue;
      rules.push({
        page: page.id,
        instanceId: section.instanceId,
        showAt: clean.showAt,
        hideAt: clean.hideAt
      });
    }
  }
  rules.sort((a, b) =>
    a.page === b.page ? a.instanceId.localeCompare(b.instanceId) : a.page.localeCompare(b.page)
  );
  return rules;
}

/**
 * A stable fingerprint of WHICH rules are currently in-window — the sites
 * Worker appends it to the HTML ETag and the cache key, so cached bodies stay
 * valid between boundaries and self-invalidate at each one (contracts §5).
 */
export function scheduleEpoch(rules, now) {
  let hash = 0x811c9dc5;
  for (const rule of rules || []) {
    const token = `${rule.instanceId}:${isInWindow(rule, now) ? 1 : 0};`;
    for (let i = 0; i < token.length; i++) {
      hash ^= token.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
  }
  return (hash >>> 0).toString(36);
}

/** Every future boundary instant a rule set carries, ascending + deduped. */
export function scheduleBoundaries(rules, after = 0) {
  const out = new Set();
  for (const rule of rules || []) {
    for (const at of [rule.showAt, rule.hideAt]) {
      if (at != null && at > after) out.add(at);
    }
  }
  return [...out].sort((a, b) => a - b);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(ms) {
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** The drawer/summary sentence: "Dec 20 → Jan 2", "From Dec 20", "Until Jan 2". */
export function scheduleSummary(schedule) {
  const clean = sanitizeSchedule(schedule);
  if (!clean) return '';
  if (clean.showAt !== null && clean.hideAt !== null) {
    return `${shortDate(clean.showAt)} → ${shortDate(clean.hideAt)}`;
  }
  if (clean.showAt !== null) return `From ${shortDate(clean.showAt)}`;
  return `Until ${shortDate(clean.hideAt)}`;
}
